import React, { useEffect, useState } from "react";
import { motion, useAnimation, AnimatePresence } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { FaMapMarkerAlt, FaCalendarAlt, FaChevronDown } from "react-icons/fa";
import { workHistory } from "../data";

const experienceVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.15,
      duration: 0.6,
      ease: "easeOut",
    },
  }),
};

const accordionVariants = {
  open: { 
    height: "auto",
    opacity: 1,
    transition: { 
      duration: 0.4,
      ease: "easeOut"
    }
  },
  closed: { 
    height: 0,
    opacity: 0,
    transition: { 
      duration: 0.3,
      ease: "easeIn"
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: (i) => ({
    opacity: 1,
    x: 0,
    transition: {
      delay: i * 0.1,
      duration: 0.4,
      ease: "easeOut",
    },
  }),
};

export default function WorkHistory() {
  const controls = useAnimation();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  const [openIndex, setOpenIndex] = useState(null);

  useEffect(() => {
    if (inView) {
      controls.start("visible");
    }
  }, [controls, inView]);

  const toggleAccordion = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <motion.section
      id="work-history"
      ref={ref}
      initial="hidden"
      animate={controls}
      className="flex items-center justify-center bg-gray-800 text-white pt-6 pb-8 md:pt-12 md:pb-12 w-full px-4 sm:px-6 md:px-8 scroll-mt-16"
    >
      <div className="w-full max-w-7xl container mx-auto p-4 md:p-12 flex flex-col items-start space-y-4 md:space-y-6 font-ntr">
        <h2 className="text-4xl mb-4 md:mb-5 font-bold text-left w-full md:w-auto">
          <span className="text-custom-accent">/ </span>Work History
        </h2>
        
        <div className="w-full space-y-4 md:space-y-8 relative">
          {/* Timeline line */}
          <div className="hidden md:block absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-custom-accent/50 via-custom-accent/30 to-transparent" />
          
          {workHistory.map((experience, companyIndex) => (
            <motion.div
              key={experience.company}
              custom={companyIndex}
              variants={experienceVariants}
              initial="hidden"
              animate={controls}
              className="relative"
            >
              {/* Timeline dot */}
              <div className="hidden md:flex absolute left-[29px] -top-1 w-6 h-6 bg-gray-800 rounded-full border-4 border-custom-accent items-center justify-center z-10">
                <div className="w-2 h-2 bg-custom-accent rounded-full" />
              </div>

              <div className="bg-gradient-to-br from-gray-700/60 to-gray-800/60 backdrop-blur-sm border border-gray-600/50 rounded-xl p-6 md:p-8 ml-0 md:ml-16 hover:border-custom-accent/50 transition-all duration-300 hover:shadow-xl hover:shadow-custom-accent/10">
                {/* Company Header */}
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-6 pb-4 border-b border-gray-600/50">
                  <div>
                    <h3 className="text-2xl md:text-3xl font-bold text-custom-accent mb-2">
                      {experience.company}
                    </h3>
                    {experience.location && (
                      <div className="flex items-center text-gray-400 text-sm md:text-base">
                        <FaMapMarkerAlt className="mr-2 text-custom-accent/70" size={14} />
                        <span>{experience.location}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Positions */}
                <div className="space-y-6">
                  {experience.positions.map((position, positionIndex) => {
                    const index = `${companyIndex}-${positionIndex}`;
                    const isOpen = openIndex === index;
                    return (
                      <motion.div
                        key={position.title}
                        custom={companyIndex + positionIndex}
                        variants={experienceVariants}
                        initial="hidden"
                        animate={controls}
                        className="bg-gray-800/50 rounded-lg border border-gray-700/50 overflow-hidden hover:border-custom-accent/30 transition-all duration-300"
                      >
                        {/* Position Header - Clickable */}
                        <div 
                          className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 sm:p-5 cursor-pointer hover:bg-gray-700/30 transition-colors duration-200"
                          onClick={() => toggleAccordion(index)}
                        >
                          <div className="flex-1">
                            <h4 className="text-lg md:text-xl font-semibold text-white mb-2 sm:mb-0">
                              {position.title}
                            </h4>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="flex items-center text-gray-400 text-sm">
                              <FaCalendarAlt className="mr-2 text-custom-accent/70" size={12} />
                              <span>{position.period}</span>
                            </div>
                            <motion.div
                              animate={{ rotate: isOpen ? 180 : 0 }}
                              transition={{ duration: 0.3 }}
                              className="text-custom-accent"
                            >
                              <FaChevronDown size={16} />
                            </motion.div>
                          </div>
                        </div>

                        {/* Accordion Content */}
                        <AnimatePresence>
                          {isOpen && (
                            <motion.div
                              initial="closed"
                              animate="open"
                              exit="closed"
                              variants={accordionVariants}
                              className="overflow-hidden"
                            >
                              <div className="px-4 sm:px-5 pb-5 pt-2 space-y-5">
                                {/* Description */}
                                <div>
                                  {Array.isArray(position.description) ? (
                                    <ul className="space-y-3">
                                      {position.description.map((item, itemIndex) => (
                                        <motion.li
                                          key={itemIndex}
                                          custom={itemIndex}
                                          variants={itemVariants}
                                          initial="hidden"
                                          animate="visible"
                                          className="flex items-start text-gray-300 leading-relaxed"
                                        >
                                          <span className="text-custom-accent mr-3 mt-1.5 flex-shrink-0">▸</span>
                                          <span className="text-sm md:text-base">{item}</span>
                                        </motion.li>
                                      ))}
                                    </ul>
                                  ) : (
                                    <p className="text-gray-300 text-sm md:text-base leading-relaxed">
                                      {position.description}
                                    </p>
                                  )}
                                </div>

                                {/* Technologies */}
                                <div>
                                  <h5 className="text-xs font-semibold text-gray-400 mb-3 uppercase tracking-wide">
                                    Technologies Used
                                  </h5>
                                  <div className="flex flex-wrap gap-2">
                                    {position.technologies.map((tech, techIndex) => (
                                      <motion.span
                                        key={tech}
                                        initial={{ opacity: 0, scale: 0.8 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        transition={{ delay: techIndex * 0.05 }}
                                        className="text-xs md:text-sm px-3 py-1.5 bg-custom-accent/10 text-custom-accent border border-custom-accent/30 rounded-md font-medium"
                                      >
                                        {tech}
                                      </motion.span>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.section>
  );
} 